/**
 * De niveaus van de portefeuillehiërarchie (A1): portefeuille, deelportefeuille,
 * complex, pand en object, en waar elk niveau naartoe gaat vanaf de plek waar
 * je bent (FB-110, Jan 31-10-2026).
 *
 * Jan: "wanneer ik op pand selecteer dan is de keuze Portefeuille,
 * deelportefeuille, complex, pand, object weg. Deze moet hoe diep je ook gaat
 * altijd aanwezig zijn zodat je altijd weer terug kan." Dus staat de balk op
 * elk scherm van Vastgoed, ook op een pand- of objectscherm, en neemt een
 * niveau de context mee waar die er is: vanaf een object gaat Pand naar het
 * pandscherm van dát pand en Complex naar dát complex, niet naar de hele lijst.
 *
 * Puur: geen React, zodat de lifecycle-schakelaar (FB-115) dezelfde niveaus en
 * labels kan gebruiken met zijn eigen bestemmingen.
 */
import { isEmptyDisplay } from "./display";

export type PortfolioLevel = "portfolio" | "sub_portfolio" | "complex" | "building" | "asset";

/**
 * De vijf niveaus in de volgorde van de hiërarchie, van boven naar beneden. In
 * het enkelvoud, zoals Jan ze noemt: het is een niveau, geen lijst (op een
 * pandscherm staat "Pand" aan, niet "Panden").
 */
export const PORTFOLIO_LEVELS: ReadonlyArray<{ level: PortfolioLevel; label: string }> = [
  { level: "portfolio", label: "Portefeuille" },
  { level: "sub_portfolio", label: "Deelportefeuille" },
  { level: "complex", label: "Complex" },
  { level: "building", label: "Pand" },
  { level: "asset", label: "Object" },
];

/**
 * Het complex en pand waar je in zit, voor zover bekend. Op een lijst komt het
 * uit de URL (`?property_complex=` of `?building=`), op een detailscherm geeft
 * de pagina het mee uit haar eigen gegevens.
 */
export type LevelContext = {
  /** De complexcode, precies één; bij meer complexen is er geen enkel complex om naar terug te gaan. */
  complex?: string | null;
  /** De BAG-pandsleutel: daarop filteren de lijsten. */
  buildingKey?: string | null;
  /** Het id van het pandscherm (`/panden/:id`); als het er is, gaat Pand daarheen. */
  buildingId?: string | null;
};

export type LevelTarget = {
  level: PortfolioLevel;
  label: string;
  to: string;
  active: boolean;
  /** Uitleg bij de knop: waar hij naartoe gaat als dat meer is dan de hele lijst. */
  title?: string;
};

/** Welk niveau hoort bij dit pad, of `null` buiten Vastgoed. */
export function levelForPath(pathname: string, hash = ""): PortfolioLevel | null {
  if (pathname === "/portefeuille") return hash === "#deelportefeuilles" ? "sub_portfolio" : "portfolio";
  if (pathname === "/complexen" || pathname.startsWith("/complexen/")) return "complex";
  if (pathname === "/panden" || pathname.startsWith("/panden/")) return "building";
  if (pathname === "/objecten" || pathname.startsWith("/objecten/")) return "asset";
  return null;
}

/** Het complex en pand uit de querystring van een lijst; alleen een eenduidige keuze telt. */
export function contextFromSearch(search: string | URLSearchParams): LevelContext {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const complexen = params.getAll("property_complex").filter(Boolean);
  const pand = params.get("building");
  return {
    complex: complexen.length === 1 ? complexen[0] : null,
    buildingKey: pand || null,
  };
}

const query = (key: string, value: string) => `?${new URLSearchParams({ [key]: value }).toString()}`;

/**
 * De vijf niveaus met hun bestemming. Het actieve niveau blijft waar je bent
 * (`current`), zodat een klik erop niets weggooit. Hoger gaat naar het
 * bovenliggende complex of pand als dat bekend is, lager naar de eenheden
 * daarvan; anders naar de hele lijst.
 */
export function levelTargets(active: PortfolioLevel | null, context: LevelContext, current: string): LevelTarget[] {
  const { complex, buildingKey, buildingId } = context;
  const bestemming = (level: PortfolioLevel): { to: string; title?: string } => {
    switch (level) {
      case "portfolio":
        return { to: "/portefeuille" };
      case "sub_portfolio":
        return { to: "/portefeuille#deelportefeuilles" };
      case "complex":
        return complex
          ? { to: `/complexen${query("property_complex", complex)}`, title: `Naar complex ${complex}` }
          : { to: "/complexen" };
      case "building":
        if (buildingId) return { to: `/panden/${buildingId}`, title: "Naar dit pand" };
        if (buildingKey) return { to: `/panden${query("building", buildingKey)}`, title: `Naar pand ${buildingKey}` };
        return complex
          ? { to: `/panden${query("property_complex", complex)}`, title: `Naar de panden van complex ${complex}` }
          : { to: "/panden" };
      case "asset":
        if (buildingKey) return { to: `/objecten${query("building", buildingKey)}`, title: "Naar de eenheden van dit pand" };
        return complex
          ? { to: `/objecten${query("property_complex", complex)}`, title: `Naar de eenheden van complex ${complex}` }
          : { to: "/objecten" };
    }
  };
  return PORTFOLIO_LEVELS.map(({ level, label }) => {
    const isActive = level === active;
    return { level, label, active: isActive, ...(isActive ? { to: current } : bestemming(level)) };
  });
}

/**
 * De context van een objectscherm uit de keten in zijn kop (`hierarchy_links`):
 * het complex linkt naar `/objecten?property_complex=<code>`, het pand naar
 * `/panden/<id>` (of, zonder pandlaag, naar `/objecten?building=<sleutel>`).
 * De links zijn al exact; hier wordt alleen gelezen waar ze heen gaan.
 */
export function contextFromHierarchyLinks(
  links: ReadonlyArray<{ level: string; value: { href?: string | null } }> | undefined,
): LevelContext {
  const href = (level: string) => links?.find((item) => item.level === level)?.value.href ?? null;
  const zoek = (link: string | null) => new URLSearchParams(link?.split("?")[1] ?? "");
  const pand = href("building");
  const pandId = pand?.match(/^\/panden\/([^/?#]+)/)?.[1] ?? null;
  return {
    complex: zoek(href("complex")).get("property_complex"),
    buildingId: pandId,
    buildingKey: pandId ? null : zoek(pand).get("building"),
  };
}

/**
 * Het complex van een pandscherm uit de kopcel "Complex": de complexcode, of
 * "35 +1" als het pand in meer complexen ligt. Dan is er niet één complex om
 * naar terug te gaan en blijft Complex de hele lijst.
 */
export function complexFromBuildingCell(cell: { display: string } | undefined): string | null {
  const display = cell?.display.trim();
  if (!display || isEmptyDisplay(display) || display.includes("+")) return null;
  return display;
}
